/**
 * 應用程式常數
 *
 * 集中管理所有「魔法數字」和硬編碼值
 * 便於維護和調整參數
 */

// ========================================================================
// Canvas 相關常數
// ========================================================================

export const CANVAS = {
  /** Canvas 最大高度（像素） */
  MAX_HEIGHT: 600,

  /** Canvas 視窗高度 */
  VIEWPORT_HEIGHT: '70vh',

  /** 線條最小長度（像素） */
  MIN_LINE_LENGTH: 20,

  /** 選取容差（像素） */
  SELECTION_TOLERANCE: 20,

  /** 箭頭大小（像素） */
  ARROW_SIZE: 6,

  /** Top row 檢測容差（像素） */
  TOP_ROW_TOLERANCE: 20,
} as const;

// ========================================================================
// 認證相關常數
// ========================================================================

export const AUTH = {
  /** 認證重試次數上限 */
  MAX_RETRIES: 10,

  /** 重試間隔（毫秒） */
  RETRY_DELAY_MS: 200,

  /** Token localStorage key */
  TOKEN_KEY: 'raiseplan_token',

  /** 使用者資訊 localStorage key */
  USER_KEY: 'raiseplan_user',

  /** IP 檢查逾時（毫秒） */
  IP_CHECK_TIMEOUT: 5000,
} as const;

// ========================================================================
// 門標註相關常數
// ========================================================================

export const DOOR = {
  /** 門類型顏色對應 */
  COLORS: {
    main: '#ef4444',      // 紅色 - 主要出入口
    normal: '#3b82f6',    // 藍色 - 一般門
    emergency: '#f59e0b', // 橘色 - 緊急出口
  },

  /** 門線條寬度（像素） */
  LINE_WIDTH: 6,

  /** 點擊偵測閾值（像素） */
  HIT_THRESHOLD: 15,

  /** 門最小長度（像素） */
  MIN_LENGTH: 20,

  /** 預設淨空距離（公尺） */
  DEFAULT_CLEARANCE: 1.2,
} as const;

// ========================================================================
// 家具相關常數
// ========================================================================

export const FURNITURE = {
  /** 辦公桌顏色 */
  DESK_COLOR: 'rgb(220, 220, 220)',

  /** 座椅顏色 */
  CHAIR_COLOR: 'rgb(180, 180, 180)',

  /** 會議室填充顏色 */
  MEETING_ROOM_COLOR: 'rgb(203, 241, 219)',

  /** 辦公桌組顏色 */
  DESK_PAIR_COLOR: 'rgb(244, 247, 3)',

  /** 選取高亮顏色 */
  SELECTION_HIGHLIGHT: 'rgba(255, 0, 0, 0.8)',

  /** 選取高亮線寬（像素） */
  SELECTION_LINE_WIDTH: 3,

  /** 旋轉手柄半徑（像素） */
  ROTATION_HANDLE_RADIUS: 8,

  /** 旋轉手柄距離（像素） */
  ROTATION_HANDLE_DISTANCE: 30,
} as const;

// ========================================================================
// 預設家具尺寸（公尺）- UI 和 API 統一使用公尺
// ========================================================================

export const DEFAULT_DIMENSIONS = {
  /** 辦公桌預設寬度（公尺） */
  DESK_WIDTH: 1.2,

  /** 辦公桌預設深度（公尺） */
  DESK_HEIGHT: 0.7,

  /** 座椅預設深度（公尺） */
  CHAIR_DEPTH: 0.5,

  /** 預設人均坪數（坪/人） */
  PEOPLE_DENSITY: 2.5,

  /** 主管室預設寬度（公尺） */
  SUPERVISOR_ROOM_WIDTH: 3.0,

  /** 主管室預設深度（公尺） */
  SUPERVISOR_ROOM_DEPTH: 3.5,

  /** 大型會議室預設寬度（公尺） */
  LARGE_MEETING_ROOM_WIDTH: 7.0,

  /** 大型會議室預設深度（公尺） */
  LARGE_MEETING_ROOM_DEPTH: 4.2,

  /** 中型會議室預設寬度（公尺） */
  MEDIUM_MEETING_ROOM_WIDTH: 5.2,

  /** 中型會議室預設深度（公尺） */
  MEDIUM_MEETING_ROOM_DEPTH: 4.2,

  /** 小型會議室預設寬度（公尺） */
  SMALL_MEETING_ROOM_WIDTH: 4.6,

  /** 小型會議室預設深度（公尺） */
  SMALL_MEETING_ROOM_DEPTH: 3.4,
} as const;

// ========================================================================
// 圖片處理相關常數
// ========================================================================

export const IMAGE = {
  /** 圖片最大寬度（像素） */
  MAX_WIDTH: 3000,

  /** JPEG 壓縮品質 */
  JPEG_QUALITY: 0.92,

  /** 預設 MIME 類型 */
  DEFAULT_MIME_TYPE: 'image/jpeg',
} as const;

// ========================================================================
// 輪詢相關常數
// ========================================================================

export const POLLING = {
  /** 輪詢間隔（毫秒） */
  INTERVAL: 1000,

  /** 輪詢逾時（毫秒） - 5 分鐘 */
  TIMEOUT: 300000,
} as const;

// ========================================================================
// UI 相關常數
// ========================================================================

export const UI = {
  /** 通知顯示時間（毫秒） */
  NOTIFICATION_DURATION: 3000,

  /** 動畫持續時間（毫秒） */
  ANIMATION_DURATION: 300,

  /** 防抖延遲（毫秒） */
  DEBOUNCE_DELAY: 150,
} as const;
